import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Link as RouterLink } from "react-router-dom";
import api from "../bootstrapApi";
import { clearCart, removeCartItem, updateCartItem, useShopCart } from "../shopCart";

const initialForm = {
  displayName: "",
  whatsappNumber: "",
  locationText: "",
  notes: "",
};

export default function ShopCartPage() {
  const accent = "#e86026";
  const accentDeep = "#c94b19";
  const support = "#656e8f";
  const cartItems = useShopCart();
  const [form, setForm] = useState(initialForm);
  const [message, setMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutAnchor, setCheckoutAnchor] = useState(null);

  const totals = useMemo(() => {
    const itemsValue = cartItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0) * Number(item.price || 0),
      0
    );
    return {
      itemCount: cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      total: itemsValue,
    };
  }, [cartItems]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await api.post("/api/public/inquiry-cart", {
        displayName: form.displayName,
        whatsappNumber: form.whatsappNumber,
        locationText: form.locationText,
        notes: form.notes || null,
        items: cartItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          notes: item.notes,
        })),
      });

      clearCart();
      setForm(initialForm);
      setShowCheckoutForm(false);
      setMessage({
        type: "success",
        text: `Merci. Votre demande a ete enregistree sous ${response.data.lead.publicReference || response.data.lead.id}.`,
      });
    } catch (_error) {
      setMessage({
        type: "error",
        text: "Le panier n'a pas pu etre transmis a l'equipe.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <Box
        sx={{
          borderRadius: "24px",
          overflow: "hidden",
          border: "1px solid #f0ddd3",
          background: "#fff",
          boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
        }}
      >
        <Box
          sx={{
            p: { xs: 2.2, md: 2.8 },
            background: `linear-gradient(90deg, ${accent} 0%, ${accentDeep} 48%, ${support} 100%)`,
            color: "#fff",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.4} justifyContent="space-between" alignItems={{ md: "center" }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Panier & validation
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.82)" }}>
                Envoyez votre demande complete avec produits, quantites et coordonnees en une seule etape.
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.76)", mt: 0.8, maxWidth: 620 }}>
                Un commercial vous contactera dans les plus brefs delais pour confirmer votre besoin. Le paiement se fait plus tard, apres validation.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.05fr 0.95fr" }, gap: 3 }}>
        <Box
          sx={{
            borderRadius: "24px",
            border: "1px solid #f0ddd3",
            background: "#fff",
            boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2.2 }}>
            <Box>
              <Typography variant="h5" sx={{ color: "#3b302f", fontWeight: 800 }}>
                Produits selectionnes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totals.itemCount} Positionen
              </Typography>
            </Box>
            <Chip label={`${totals.total.toFixed(2)} EUR`} sx={{ borderRadius: "12px", fontWeight: 800 }} />
          </Stack>

          <Divider />

          {cartItems.length === 0 ? (
            <Box sx={{ px: 3, py: 5 }}>
              <Typography variant="body1" color="text.secondary">
                Le panier est vide. Ajoutez d'abord des produits depuis la boutique.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 1.4, p: 2.4 }}>
              {cartItems.map((item) => (
                <Box
                  key={item.productId}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "96px 1fr auto", md: "120px 1fr auto" },
                    gap: 1.6,
                    alignItems: "center",
                    p: 1.6,
                    borderRadius: "18px",
                    border: "1px solid #f0ddd3",
                    background: "#fffdf9",
                  }}
                >
                  <Box
                    sx={{
                      height: { xs: 78, md: 92 },
                      borderRadius: "14px",
                      backgroundImage: item.imageUrl
                        ? `url(${item.imageUrl})`
                        : "linear-gradient(135deg, rgba(23,39,61,0.12), rgba(255,183,3,0.18))",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      bgcolor: "#fff",
                    }}
                  />

                  <Box sx={{ display: "grid", gap: 0.6 }}>
                    <Typography sx={{ color: "#3b302f", fontWeight: 800 }}>{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.categoryName || "Produkt"} · {Number(item.price || 0).toFixed(2)} {item.currency || "EUR"}
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        size="small"
                        type="number"
                        label="Quantite"
                        value={item.quantity}
                        onChange={(event) =>
                          updateCartItem(item.productId, { quantity: Number(event.target.value || 1) })
                        }
                        sx={{ maxWidth: 120 }}
                      />
                      <TextField
                        size="small"
                        label="Note"
                        value={item.notes || ""}
                        onChange={(event) => updateCartItem(item.productId, { notes: event.target.value })}
                      />
                    </Stack>
                  </Box>

                  <IconButton onClick={() => removeCartItem(item.productId)} sx={{ justifySelf: "end" }}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Box>
              ))}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ pt: 0.8 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowCheckoutForm(true);
                    setTimeout(() => {
                      checkoutAnchor?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 0);
                  }}
                  sx={{ flex: 1, bgcolor: accent, color: "#fff", fontWeight: 800, py: 1.3, "&:hover": { bgcolor: accentDeep } }}
                >
                  Valider la commande
                </Button>
                <Button
                  component={RouterLink}
                  to="/shop"
                  variant="outlined"
                  sx={{ flex: 1, borderRadius: "14px", fontWeight: 800 }}
                >
                  Continuer vos achats
                </Button>
              </Stack>
            </Box>
          )}
        </Box>

        {showCheckoutForm ? (
          <Box
            ref={setCheckoutAnchor}
            sx={{
              borderRadius: "24px",
              border: "1px solid #f0ddd3",
              background: "#fff",
              boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
              p: 3,
              display: "grid",
              gap: 1.5,
              alignContent: "start",
            }}
          >
            <Typography variant="h5" sx={{ color: "#3b302f", fontWeight: 800 }}>
              Finaliser la demande
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Renseignez vos coordonnees une seule fois pour envoyer toute la commande comme lead ouvert.
            </Typography>

            <TextField
              label="Nom"
              value={form.displayName}
              onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
            />
            <TextField
              label="Numero WhatsApp"
              value={form.whatsappNumber}
              onChange={(event) => setForm((current) => ({ ...current, whatsappNumber: event.target.value }))}
            />
            <TextField
              label="Quartier"
              value={form.locationText}
              onChange={(event) => setForm((current) => ({ ...current, locationText: event.target.value }))}
            />
            <TextField
              label="Note de projet"
              multiline
              minRows={4}
              value={form.notes}
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            />

            <Divider />

            <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.5 }}>
              <Typography sx={{ fontWeight: 800, color: "#3b302f" }}>Total</Typography>
              <Typography sx={{ fontWeight: 800, color: "#3b302f" }}>${totals.total.toFixed(2)}</Typography>
            </Stack>

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={cartItems.length === 0 || !form.displayName || !form.whatsappNumber || isSubmitting}
              sx={{ mt: 1, bgcolor: accent, color: "#fff", fontWeight: 800, py: 1.4, "&:hover": { bgcolor: accentDeep } }}
            >
              Envoyer la demande
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
